import { useCallback, useRef, useEffect } from 'react';
import {
  isViewTransitionSupported,
  startViewTransition,
  navigateWithTransition,
  shouldSkipTransition,
  setViewTransitionName,
  clearViewTransitionName,
  ViewTransitionCallback,
} from '@/client/utils/browser/viewTransitions';

type TransitionType = 'default' | 'slide' | 'fade' | 'cross-fade';

interface UseViewTransitionOptions {
  enabled?: boolean;
  respectReducedMotion?: boolean;
  transitionType?: TransitionType;
}

interface UseViewTransitionReturn {
  isSupported: boolean;
  isEnabled: boolean;
  startTransition: (callback: ViewTransitionCallback) => Promise<void>;
  navigate: (callback: ViewTransitionCallback) => Promise<void>;
  setTransitionName: (element: HTMLElement | null, name: string) => void;
  clearTransitionName: (element: HTMLElement | null) => void;
}

export const useViewTransition = (options: UseViewTransitionOptions = {}): UseViewTransitionReturn => {
  const { enabled = true, respectReducedMotion = true, transitionType = 'default' } = options;

  const isSupported = isViewTransitionSupported();
  const isEnabled = !shouldSkipTransition({ enabled, respectReducedMotion });

  const startTransition = useCallback(
    async (callback: ViewTransitionCallback): Promise<void> => {
      await startViewTransition(callback, {
        skipTransition: !isEnabled,
      });
    },
    [isEnabled],
  );

  const navigate = useCallback(
    async (callback: ViewTransitionCallback): Promise<void> => {
      await navigateWithTransition(callback, {
        enabled,
        respectReducedMotion,
        transitionType,
      });
    },
    [enabled, respectReducedMotion, transitionType],
  );

  const setTransitionName = useCallback((element: HTMLElement | null, name: string): void => {
    setViewTransitionName(element, name);
  }, []);

  const clearTransitionName = useCallback((element: HTMLElement | null): void => {
    clearViewTransitionName(element);
  }, []);

  return {
    isSupported,
    isEnabled,
    startTransition,
    navigate,
    setTransitionName,
    clearTransitionName,
  };
};

export const useViewTransitionRef = <T extends HTMLElement>(name: string) => {
  const prevElRef = useRef<T | null>(null);

  const setRef = useCallback(
    (node: T | null) => {
      if (prevElRef.current && prevElRef.current !== node) {
        clearViewTransitionName(prevElRef.current);
      }

      if (node) {
        setViewTransitionName(node, name);
      }

      prevElRef.current = node;
    },
    [name],
  );

  useEffect(() => {
    return () => {
      if (prevElRef.current) {
        clearViewTransitionName(prevElRef.current);
        prevElRef.current = null;
      }
    };
  }, []);

  return setRef;
};

export default useViewTransition;
